/**
 * Composición de la lámina final: cabecera + columna de bloques + mapa
 * enmarcado + pie.
 *
 * El orden importa. La columna se mide entera ANTES de dibujar para poder
 * repartir el espacio sobrante como aire uniforme entre bloques; y el recorte
 * del mapa se ajusta a la proporción del hueco disponible con escala UNIFORME,
 * porque estirar el raster convertiría el círculo de 150 m en un óvalo y la
 * lámina dejaría de ser una pieza técnica creíble.
 */

import L from 'leaflet';
import { destinationPoint } from '../domain/geo.js';
import { formatArea, monthYear } from '../domain/document.js';
import { EXPORT_SCALE } from '../config.js';
import { drawCompass, drawFrame } from './canvas-utils.js';
import { fitCrop } from './fit-crop.js';
import { missingDeaCount } from '../domain/compliance.js';
import { numberVerticalEquipment } from '../domain/numbering.js';
import {
  buildingRows,
  defaultBoxPosition,
  drawBuildingBox,
  measureBuildingBox,
} from './building-box.js';
import {
  complianceHeight,
  drawCompliance,
  drawElevation,
  drawHorizontal,
  drawLegend,
  drawTitleBlock,
  drawTotal,
  elevationHeight,
  horizontalHeight,
  legendHeight,
  titleBlockHeight,
  totalHeight,
} from './blocks.js';

const HEADER_H = 200;
const FOOTER_H = 64;
const COLUMN_W = 320;
const MAP_W = 820;
const COLUMN_X = 30;
const TOP_PAD = 24;
const BOTTOM_PAD = 24;
const MIN_GAP = 16;

/** Margen del satélite: 16 px parejo, salvo a la derecha donde 14 los ocupa la cenefa. */
const MARGIN = { top: 16, left: 16, bottom: 16, right: 30 };

/**
 * Subtítulo dinámico. Con algún DEA propuesto la lámina es un dimensionamiento;
 * con todos adquiridos documenta lo instalado. Decirlo mal cambia el valor legal
 * del documento.
 */
export function sheetSubtitle(deas) {
  const hasProposed = !deas.length || deas.some((dea) => dea.estado === 'propuesto');
  return hasProposed
    ? 'ESTIMACIÓN DE EQUIPOS DEA REQUERIDOS SEGÚN SUPERFICIE'
    : 'COBERTURA DE EQUIPOS DEA INSTALADOS';
}

/** Recorte del mapa fuente que contiene todo el trabajo, con margen. */
function contentCrop(source, sheet, deaItems, buildingItems, radius) {
  const { toPoint, width: srcW, height: srcH } = source;
  let minX = 1e9;
  let minY = 1e9;
  let maxX = -1e9;
  let maxY = -1e9;
  const extend = (x, y) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };

  deaItems.forEach((dea) => {
    const center = dea.circle.getLatLng();
    const centerPoint = toPoint(center);
    const edge = destinationPoint(center.lat, center.lng, radius, dea.bearing);
    const edgePoint = toPoint(L.latLng(edge[0], edge[1]));
    const radiusPx = Math.hypot(edgePoint.x - centerPoint.x, edgePoint.y - centerPoint.y);
    extend(centerPoint.x - radiusPx, centerPoint.y - radiusPx);
    extend(centerPoint.x + radiusPx, centerPoint.y + radiusPx);
  });
  sheet.perimeter.forEach((point) => {
    const p = toPoint(L.latLng(point[0], point[1]));
    extend(p.x, p.y);
  });
  buildingItems.forEach((building) => {
    const p = toPoint(building.marker.getLatLng());
    extend(p.x - 22, p.y - 22);
    extend(p.x + 22, p.y + 22);
  });

  const pad = 60;
  return {
    x0: Math.max(0, minX - pad),
    y0: Math.max(0, minY - pad),
    x1: Math.min(srcW, maxX + pad),
    y1: Math.min(srcH, maxY + pad),
  };
}

/**
 * @param {object} source Resultado de `renderMapSource`.
 * @param {object} sheet  Estado de la lámina para los bloques.
 * @param {object} deps   { map, deaItems, buildingItems, logoImage }
 * @returns {HTMLCanvasElement}
 */
export function composeSheet(
  source,
  sheet,
  { map, deaItems, buildingItems, logoImage, layout = {} },
) {
  const scale = EXPORT_SCALE;
  const { canvas: src, width: srcW, height: srcH, toPoint } = source;

  let { x0, y0, x1, y1 } = contentCrop(source, sheet, deaItems, buildingItems, sheet.radius);
  let cropW = Math.max(20, x1 - x0);
  let cropH = Math.max(20, y1 - y0);

  // Sólo los edificios que NO cumplen llevan caja de elevación. El veredicto de
  // los que cumplen ya sale en Cumplimiento Normativo, y repetirlo llena la
  // cenefa de cajas verdes que no piden ninguna acción.
  const nonCompliant = sheet.buildings.filter((building) => missingDeaCount(building) > 0);
  const numbering = numberVerticalEquipment(sheet.buildings, sheet.deas.length);

  // 1) Medir la columna completa antes de dibujar nada.
  const columnW = COLUMN_W - COLUMN_X - 14;
  const blockHeights = [
    totalHeight(sheet),
    complianceHeight(sheet, columnW),
    horizontalHeight(sheet),
    ...nonCompliant.map((building) => elevationHeight(building)),
    legendHeight(sheet),
    titleBlockHeight(sheet),
  ];
  const blockCount = blockHeights.length;
  const blocksSum = blockHeights.reduce((sum, height) => sum + height, 0);
  const columnNeeded = TOP_PAD + blocksSum + (blockCount - 1) * MIN_GAP + BOTTOM_PAD;

  // 2) El cuerpo es lo más alto entre el mapa a su proporción natural y la columna.
  const naturalMapH = Math.round((MAP_W * cropH) / cropW);
  const bodyH = Math.max(naturalMapH, columnNeeded);

  const mapX = COLUMN_W + MARGIN.left;
  const mapY = HEADER_H + MARGIN.top;
  const drawW = MAP_W - MARGIN.left - MARGIN.right;
  const drawH = bodyH - MARGIN.top - MARGIN.bottom;

  // 3) Resolver qué trozo del canvas fuente se dibuja y dónde, con la garantía
  //    de que el contenido obligatorio no se corta nunca.
  const fit = fitCrop({ x0, y0, x1, y1 }, srcW, srcH, drawW, drawH);
  const mapRect = {
    x: mapX + (drawW - fit.destW) / 2,
    y: mapY + (drawH - fit.destH) / 2,
    w: fit.destW,
    h: fit.destH,
  };

  const outW = COLUMN_W + MAP_W;
  const outH = HEADER_H + bodyH + FOOTER_H;
  const out = document.createElement('canvas');
  out.width = outW * scale;
  out.height = outH * scale;
  const ctx = out.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#0e1820';
  ctx.fillRect(0, 0, outW, outH);

  // ---- Cabecera ----
  ctx.fillStyle = '#14202b';
  ctx.fillRect(0, 0, outW, HEADER_H);

  if (logoImage?.complete && logoImage.naturalWidth) {
    // Zona segura del logo: nunca toca el filete blanco del marco.
    const safeX0 = 30;
    const safeY0 = 30;
    const safeX1 = COLUMN_W - 20;
    const safeY1 = HEADER_H - 22;
    const safeW = safeX1 - safeX0;
    const safeH = safeY1 - safeY0;
    const logoScale = Math.min(safeW / logoImage.naturalWidth, safeH / logoImage.naturalHeight);
    const logoW = logoImage.naturalWidth * logoScale;
    const logoH = logoImage.naturalHeight * logoScale;
    ctx.filter = 'brightness(0) invert(1)';
    try {
      ctx.drawImage(
        logoImage,
        safeX0 + (safeW - logoW) / 2,
        safeY0 + (safeH - logoH) / 2,
        logoW,
        logoH,
      );
    } catch {
      /* logo no dibujable */
    }
    ctx.filter = 'none';
  }

  const midMap = mapRect.x + mapRect.w / 2;
  const surface = sheet.areaM2 > 0 ? formatArea(sheet.areaM2) : '—';
  const headerLines = [
    { text: sheet.meta.sitio || '', font: 'bold 34px Arial', color: '#fff', lineHeight: 42 },
  ];
  if (sheet.meta.sede) {
    headerLines.push({
      text: sheet.meta.sede,
      font: 'bold 24px Arial',
      color: '#cfe0ee',
      lineHeight: 32,
    });
  }
  headerLines.push({
    text: `${sheet.meta.direccion ? `${sheet.meta.direccion}    /    ` : ''}Superficie: ${surface}`,
    font: 'bold 14px Arial',
    color: '#ffe9a8',
    lineHeight: 22,
  });
  headerLines.push({
    text: sheetSubtitle(sheet.deas),
    font: 'bold 14px Arial',
    color: '#9fd3b3',
    lineHeight: 26,
  });

  let headerY = (HEADER_H - headerLines.reduce((sum, line) => sum + line.lineHeight, 0)) / 2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  headerLines.forEach((line) => {
    headerY += line.lineHeight / 2;
    ctx.fillStyle = line.color;
    ctx.font = line.font;
    ctx.fillText(line.text, midMap, headerY);
    headerY += line.lineHeight / 2;
  });

  // ---- Columna: fondo y divisor ----
  ctx.fillStyle = '#111c26';
  ctx.fillRect(0, HEADER_H, COLUMN_W, bodyH);
  ctx.strokeStyle = 'rgba(255,255,255,.12)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(COLUMN_W, HEADER_H);
  ctx.lineTo(COLUMN_W, HEADER_H + bodyH);
  ctx.stroke();

  // ---- Mapa: escala uniforme, contain. Ver fitCrop: jamás recorta contenido ----
  ctx.fillStyle = '#0e1820';
  ctx.fillRect(mapX, mapY, drawW, drawH);
  ctx.drawImage(
    src,
    fit.srcX * scale,
    fit.srcY * scale,
    fit.srcW * scale,
    fit.srcH * scale,
    mapRect.x,
    mapRect.y,
    mapRect.w,
    mapRect.h,
  );
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 3;
  ctx.strokeRect(mapRect.x, mapRect.y, mapRect.w, mapRect.h);

  // ---- Cuadros de edificio, como CAPA sobre el mapa ----
  // Se dibujan acá y no en el raster para que el operador pueda moverlos y
  // redimensionarlos en la vista previa sin re-renderizar las teselas.
  const toSheet = (point) => ({
    x: mapRect.x + ((point.x - fit.srcX) * mapRect.w) / fit.srcW,
    y: mapRect.y + ((point.y - fit.srcY) * mapRect.h) / fit.srcH,
  });

  const boxRects = [];
  (source.buildingAnchors || []).forEach((anchor) => {
    const building = sheet.buildings[anchor.index];
    if (!building) return;
    const rows = buildingRows(building, numbering, anchor.index);
    if (!rows.length) return;

    const anchorPoint = toSheet(anchor);
    const override = layout.boxes?.[anchor.index] || {};
    const scale = override.scale ?? layout.boxScale ?? 1;
    const natural = measureBuildingBox(ctx, building, rows);
    const base = defaultBoxPosition(anchorPoint, {
      width: natural.width * scale,
      height: natural.height * scale,
    });
    const position = {
      x: (override.x ?? base.x) + (override.dx || 0),
      y: (override.y ?? base.y) + (override.dy || 0),
    };

    const rect = drawBuildingBox(ctx, building, rows, anchorPoint, position, scale);
    boxRects.push({ index: anchor.index, ...rect, scale, anchor: anchorPoint });
  });

  // Descargo sobre el radio: es una referencia normativa, no una promesa de
  // alcance efectivo. Va una sola vez y sobre el mapa, donde se lo lee.
  {
    const note =
      'Radio referencial (Art. 5º · D.S. 56/2020). El alcance efectivo depende de accesos y obstáculos.';
    ctx.font = 'italic 11px Arial';
    const textWidth = ctx.measureText(note).width;
    const boxH = 24;
    const noteX = mapRect.x + 14;
    const noteY = mapRect.y + mapRect.h - 14 - boxH;
    ctx.fillStyle = 'rgba(16,26,36,.74)';
    ctx.strokeStyle = 'rgba(255,255,255,.25)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(noteX, noteY, textWidth + 20, boxH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#dfe6ea';
    ctx.fillText(note, noteX + 10, noteY + boxH / 2);
  }

  // Rosa de los vientos, orientada al norte real de la imagen rotada.
  {
    const boxW = 80;
    const boxH = 90;
    const compassX = mapRect.x + mapRect.w - boxW - 14;
    const compassY = mapRect.y + 14;
    ctx.fillStyle = 'rgba(16,26,36,.6)';
    ctx.strokeStyle = 'rgba(255,255,255,.6)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.roundRect(compassX, compassY, boxW, boxH, 8);
    ctx.fill();
    ctx.stroke();
    const center = map.getCenter();
    const centerPoint = toPoint(center);
    const northPoint = toPoint(L.latLng(...destinationPoint(center.lat, center.lng, 100, 0)));
    drawCompass(
      ctx,
      compassX + boxW / 2,
      compassY + boxH / 2,
      24,
      Math.atan2(northPoint.y - centerPoint.y, northPoint.x - centerPoint.x),
    );
  }

  // ---- Bloques de la columna ----
  const gap = Math.max(MIN_GAP, (bodyH - TOP_PAD - BOTTOM_PAD - blocksSum) / (blockCount - 1));
  let cursorY = HEADER_H + TOP_PAD;

  cursorY = drawTotal(ctx, COLUMN_X, cursorY, columnW, sheet) + gap;

  const afterCompliance = drawCompliance(ctx, COLUMN_X, cursorY, columnW, sheet);
  // Conector que explica que lo que sigue es el desglose por dimensión.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#8fa6ba';
  ctx.font = 'italic 9.5px Arial';
  ctx.fillText(
    '▾  desglose del cumplimiento por dimensión',
    COLUMN_X + columnW / 2,
    afterCompliance + gap / 2,
  );
  cursorY = afterCompliance + gap;

  cursorY = drawHorizontal(ctx, COLUMN_X, cursorY, columnW, sheet) + gap;

  nonCompliant.forEach((building) => {
    const index = sheet.buildings.indexOf(building);
    const box = drawElevation(ctx, COLUMN_X, cursorY, columnW, building, numbering, index);
    // Badge E1, E2… que enlaza el bloque con el pictograma del mapa.
    ctx.beginPath();
    ctx.arc(box.x + 12, box.y, 11, 0, 2 * Math.PI);
    ctx.fillStyle = '#2f6db3';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`E${index + 1}`, box.x + 12, box.y);
    cursorY = box.y + box.h + gap;
  });

  cursorY = drawLegend(ctx, COLUMN_X, cursorY, columnW, sheet) + gap;
  drawTitleBlock(ctx, COLUMN_X, cursorY, columnW, sheet);

  // ---- Pie: escala gráfica y rótulo ----
  ctx.fillStyle = '#14202b';
  ctx.fillRect(0, outH - FOOTER_H, outW, FOOTER_H);
  const center = map.getCenter();
  const centerPoint = toPoint(center);
  const eastPoint = toPoint(L.latLng(...destinationPoint(center.lat, center.lng, 100, 90)));
  const sourceMetersPerPx =
    100 / Math.max(1, Math.hypot(eastPoint.x - centerPoint.x, eastPoint.y - centerPoint.y));
  // Metros por píxel EN LA LÁMINA = metros/px del fuente × px de fuente por px
  // dibujado. Antes se aproximaba con cropW/MAP_W, que ignoraba el margen y el
  // ajuste real: la barra de escala mentía sobre el papel impreso.
  drawFooter(ctx, outW, outH, sheet, (sourceMetersPerPx * fit.srcW) / mapRect.w);

  drawFrame(ctx, outW, outH);
  // Los rectángulos vuelven junto al canvas: la vista previa los necesita para
  // saber qué cuadro agarró el operador.
  out.boxRects = boxRects;
  return out;
}

/** Escala gráfica segmentada a la izquierda y el rótulo de la lámina al centro. */
function drawFooter(ctx, outW, outH, sheet, metersPerPx) {
  const cy = outH - FOOTER_H / 2;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 11px Arial';
  ctx.fillText(
    `Lámina ${sheet.meta.numLamina}   ·   ${monthYear(sheet.date)}   ·   Ley 21.156 de Cardioprotección   ·   Elaboró: VitalHeart`,
    outW / 2,
    cy,
  );

  // Se elige la mayor longitud "redonda" que quepa en ~120 px: una escala con
  // números feos (137 m) no la usa nadie para medir sobre el papel.
  const nice = [25, 50, 100, 150, 200, 250, 300, 500, 750, 1000, 1500, 2000];
  let chosen = nice[0];
  nice.forEach((value) => {
    if (value <= 120 * metersPerPx) chosen = value;
  });

  const barPx = chosen / metersPerPx;
  const barX = 24;
  const barY = cy + 3;
  const segments = 4;
  const segmentW = barPx / segments;
  const barH = 6;
  for (let i = 0; i < segments; i++) {
    ctx.fillStyle = i % 2 ? '#14202b' : '#fff';
    ctx.fillRect(barX + i * segmentW, barY, segmentW, barH);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.strokeRect(barX + i * segmentW, barY, segmentW, barH);
  }
  ctx.textAlign = 'left';
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 9px Arial';
  ctx.fillText('0', barX - 1, barY - 6);
  ctx.textAlign = 'center';
  ctx.fillText(chosen >= 1000 ? `${chosen / 1000} km` : `${chosen} m`, barX + barPx, barY - 6);
}
